import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button, Panel } from '@/design/components';
import { errorMessage } from '@/lib/errors';
import { useCourseMutations } from './api';
import { QuestionsEditor } from './QuestionsEditor';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/** חידון הלומדה: שאלות, מסיחים, והסבר לתשובה (PRD 5.6.1). */
export function QuizBuilder({
  courseId,
  quiz,
  onChanged,
}: {
  courseId: string;
  quiz: { id: string; title: string; pass_score: number; shuffle: boolean } | null;
  onChanged: () => void;
}) {
  const { createQuiz, updateQuiz } = useCourseMutations(courseId);
  const [error, setError] = useState<string | null>(null);

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  if (!quiz) {
    return (
      <Panel title="חידון">
        <div className="grid gap-3">
          <p className="m-0 text-body-sm text-muted">
            אין חידון ללומדה הזו. חידון הוא רשות, אבל לומדה שיש לה חידון נחשבת גמורה רק אחרי
            שעוברים אותו.
          </p>
          <div>
            <Button
              onClick={() =>
                run(async () => {
                  await createQuiz.mutateAsync('חידון הלומדה');
                  onChanged();
                })
              }
            >
              <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
              יצירת חידון
            </Button>
          </div>
          {error && (
            <p role="alert" className="m-0 text-body-sm text-bad">
              {error}
            </p>
          )}
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="חידון">
      <div className="grid gap-3">
        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        <form
          className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            run(async () => {
              await updateQuiz.mutateAsync({
                id: quiz.id,
                patch: {
                  title: String(form.get('quiz-title') ?? '').trim(),
                  pass_score: Number(form.get('pass') ?? 0),
                  shuffle: form.get('shuffle') === 'on',
                },
              });
              onChanged();
            });
          }}
        >
          <div className="grid gap-1">
            <label htmlFor="quiz-title" className="text-body-sm text-muted">
              שם החידון
            </label>
            <input
              id="quiz-title"
              name="quiz-title"
              defaultValue={quiz.title}
              className={FIELD}
            />
          </div>
          <div className="grid gap-1">
            <label htmlFor="pass" className="text-body-sm text-muted">
              ציון עובר
            </label>
            <input
              id="pass"
              name="pass"
              type="number"
              min={0}
              max={100}
              defaultValue={quiz.pass_score}
              className={`${FIELD} w-24`}
            />
          </div>
          <Button variant="primary" type="submit">
            שמירה
          </Button>
          <label className="flex items-center gap-2 text-body sm:col-span-3">
            <input
              type="checkbox"
              name="shuffle"
              defaultChecked={quiz.shuffle}
              className="size-5"
            />
            ערבוב סדר השאלות והתשובות
          </label>
        </form>

        <QuestionsEditor quizId={quiz.id} />
      </div>
    </Panel>
  );
}
